import { useEffect, useLayoutEffect, useRef } from 'react'
import { gsap, registerGsap } from '../../anim/gsapSetup'
import { Icon } from '../common/Icon'
import { Button } from '../common/Button'
import { FilterPanel } from './FilterPanel'
import type { FiltersState, FilterGroup } from './filterState'
import { stopScroll, startScroll } from '../../anim/smoothScroll'
import type { FilterFacets } from '../../services/productRepository'

interface MobileFilterDrawerProps {
  open: boolean
  onClose: () => void
  facets: FilterFacets
  state: FiltersState
  onToggle: (group: FilterGroup, value: string) => void
  onClearAll: () => void
  resultCount: number
}

export function MobileFilterDrawer({ open, onClose, facets, state, onToggle, onClearAll, resultCount }: MobileFilterDrawerProps) {
  const ref = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  useLayoutEffect(() => {
    if (!open) return
    registerGsap()
    stopScroll()
    document.body.style.overflow = 'hidden'
    const ctx = gsap.context(() => {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      gsap.from('[data-drawer="scrim"]', { opacity: 0, duration: 0.3 })
      gsap.from('[data-drawer="panel"]', {
        yPercent: reduced ? 0 : 100,
        opacity: reduced ? 0 : 1,
        duration: reduced ? 0.2 : 0.45,
        ease: 'power4.out',
      })
    }, ref)
    closeRef.current?.focus()
    return () => {
      ctx.revert()
      document.body.style.overflow = ''
      startScroll()
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div ref={ref} className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true" aria-label="Filters">
      <div data-drawer="scrim" onClick={onClose} className="absolute inset-0 bg-night/50 backdrop-blur-sm" />
      <div
        data-drawer="panel"
        className="absolute inset-x-0 bottom-0 flex max-h-[85vh] flex-col rounded-t-2xl bg-canvas shadow-lift"
      >
        <div className="flex items-center justify-between border-b border-sand-200 px-5 py-4">
          <h2 className="font-display text-lg font-semibold text-ink">Filter products</h2>
          <button
            ref={closeRef}
            onClick={onClose}
            aria-label="Close filters"
            className="flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-sand-100"
          >
            <Icon name="X" className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <FilterPanel facets={facets} state={state} onToggle={onToggle} onClearAll={onClearAll} />
        </div>
        <div className="border-t border-sand-200 p-4">
          <Button variant="primary" size="lg" className="w-full" onClick={onClose}>
            Show {resultCount} {resultCount === 1 ? 'product' : 'products'}
          </Button>
        </div>
      </div>
    </div>
  )
}
